import { useQueryClient } from '@tanstack/react-query';
import { Activity, ArrowLeft, BookOpen, Clapperboard, LayoutDashboard, LogOut, Settings } from 'lucide-react';
import type { ReactNode } from 'react';
import { NavLink } from 'react-router';

import { api, keys, useMeta, useSession } from '@/api';
import { SPACE, SPACE_LABEL, SPACE_PAGE } from '@/lib/space';

import { Button, cx } from './ui';

const NAV = [
	{ to: '/', label: 'Overview', icon: LayoutDashboard, end: true },
	{ to: '/instances', label: 'Sensors', icon: Activity },
	{ to: '/definitions', label: 'Catalogue', icon: BookOpen },
	{ to: '/scenarios', label: 'Scenarios', icon: Clapperboard },
	{ to: '/settings', label: 'Settings', icon: Settings },
];

export function Layout({ children }: { children: ReactNode }) {
	const meta = useMeta();
	const session = useSession();
	const qc = useQueryClient();
	const logout = async () => {
		await api.DELETE('/api/v1/session');
		qc.invalidateQueries({ queryKey: keys.session });
	};
	return (
		<div className="flex min-h-dvh flex-col md:flex-row">
			<aside className="flex shrink-0 items-center gap-1 border-b border-border bg-surface px-3 py-2 md:w-56 md:flex-col md:items-stretch md:border-r md:border-b-0 md:px-3 md:py-4">
				<div className="mr-2 flex items-center gap-2 px-2 md:mr-0 md:mb-5">
					<img src="/favicon.svg" alt="" className="size-6" />
					<span className="hidden font-semibold tracking-tight whitespace-nowrap sm:inline">Mock Sensors</span>
					<span
						className={cx(
							'shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold',
							SPACE === 'pro' ? 'bg-amber-500/15 text-amber-700 dark:text-amber-300' : 'bg-accent-soft text-accent',
						)}
					>
						{SPACE_LABEL}
					</span>
				</div>
				<a
					href={SPACE_PAGE}
					className="mr-1 flex items-center gap-2 rounded-md px-2.5 py-1.5 text-xs whitespace-nowrap text-fg-muted transition hover:bg-muted hover:text-fg md:mr-0 md:mb-2"
				>
					<ArrowLeft className="size-3.5" /> <span className="hidden md:inline">{SPACE === 'pro' ? 'SLIMS PRO page' : 'SLIMS Ambient page'}</span>
				</a>
				<nav className="flex flex-1 gap-1 overflow-x-auto md:flex-col">
					{NAV.map(({ to, label, icon: Icon, end }) => (
						<NavLink
							key={to}
							to={to}
							end={end}
							className={({ isActive }) =>
								cx(
									'flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm whitespace-nowrap transition',
									isActive ? 'bg-accent-soft font-medium text-accent' : 'text-fg-muted hover:bg-muted hover:text-fg',
								)
							}
						>
							<Icon className="size-4" />
							<span className="hidden sm:inline">{label}</span>
						</NavLink>
					))}
				</nav>
				<div className="flex items-center gap-2 md:mt-4 md:flex-col md:items-stretch">
					{session.data?.authRequired && (
						<Button variant="ghost" size="sm" onClick={logout} className="md:justify-start">
							<LogOut className="size-3.5" /> <span className="hidden md:inline">Log out</span>
						</Button>
					)}
					{meta.data && <span className="hidden px-2 text-[11px] text-fg-muted md:block">v{meta.data.version}</span>}
				</div>
			</aside>
			<main className="min-w-0 flex-1 px-4 py-6 md:px-8">
				<div className="mx-auto max-w-6xl">{children}</div>
			</main>
		</div>
	);
}
